/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';

import Button from '@userzoomlibraries/toolkit-button';
import Tooltip from '@userzoomlibraries/toolkit-tooltip';
import StudyLegal from '@userzoomplatform/pe-study-legal';
import StudyLayout, { useDevice } from '@userzoomplatform/pe-study-layout';
import { useTranslation } from 'react-i18next';
import { usePageView, useToken } from '../../../../hooks';
import { Namespaces } from '../../../../utils/i18n';
import { closeIframe } from '../../../../utils/post-messages';
import { inIframe } from '../../../../utils/iframe';

type Props = {
	moveNext?: () => void;
	content: string;
	consents: string[];
};

const defaultProps = {
	moveNext: () => null,
};

interface ConsentResult {
	consent: React.ReactText;
	checked: boolean | undefined;
}

const acceptLegalMutation = gql`
	mutation acceptLegal($input: AcceptLegalInput!) {
		acceptLegal(input: $input)
	}
`;

const LegalPage = ({ moveNext, content, consents }: Props): ReactElement => {
	const { t } = useTranslation(Namespaces.Common);
	const [consentStatus, setConsentStatus] = useState({});
	const token = useToken();
	const { device } = useDevice();
	usePageView('/legal-page');

	const [acceptLegal, { loading, called, error }] = useMutation(acceptLegalMutation, {
		context: { headers: { authorization: `Bearer ${token}` } },
		variables: { input: { content, consents } },
	});

	useEffect(() => {
		if (moveNext && !loading && called && !error) {
			moveNext();
		}
	}, [loading, called, error, moveNext]);

	useEffect(() => {
		setConsentStatus(consents.reduce((prev, current) => ({ ...prev, [current]: false }), {}));
	}, [consents]);

	const handleConsent = (consent: ConsentResult) => {
		setConsentStatus({ ...consentStatus, [consent.consent]: consent.checked });
	};

	const nextEnabled = Object.values(consentStatus).reduce((prev, current) => prev && current, true);
	return (
		<StudyLayout device={device}>
			<StudyLayout.Header exitText={inIframe ? t(`${Namespaces.Common}:cta.quit`) : ''} onExitClick={closeIframe} />
			<StudyLayout.Content>
				<StudyLegal content={content} consents={consents} onConsentClick={(event, consent) => handleConsent(consent)} />
			</StudyLayout.Content>
			<StudyLayout.Footer>
				{nextEnabled ? (
					<Button disabled={called} onClick={acceptLegal}>
						{t(`${Namespaces.Common}:cta.agreeAndContinue`)}
					</Button>
				) : (
					<Tooltip content={t(`${Namespaces.Common}:contextualHelp.checkAllRequiredOptions`)} placement="top">
						<span>
							<Button disabled>{t(`${Namespaces.Common}:cta.agreeAndContinue`)}</Button>
						</span>
					</Tooltip>
				)}
			</StudyLayout.Footer>
			{!inIframe && <StudyLayout.FooterText />}
		</StudyLayout>
	);
};

LegalPage.defaultProps = defaultProps;

export default LegalPage;
